/**
 * The ONLY code in web/ that reads a streaming response
 * (docs/design/web/WEB_STREAMING_DESIGN.md).
 *
 *   for await (const event of streamOperation(opId, { signal })) { ... }
 *
 * - fetch (not EventSource, which can't send the bearer token), parsed by
 *   eventsource-parser (spec SSE: ids, multi-line data, comments, CRLF).
 * - Each frame is validated against the generated contract for its event
 *   name; unknown/malformed frames are reported and skipped, never thrown.
 * - Resumes after a dropped connection with `last_event_id` (+ backoff),
 *   until a terminal event. 4xx responses are never retried.
 * - An event that skips sequence numbers isn't applied: the stream reopens
 *   from the last one it has. Skipped again, the server's buffer no longer
 *   has those events: with `onReset`, it starts over from the beginning,
 *   whose replay is complete (what the buffer dropped comes first); a third
 *   time (a hole in the operation's own numbering), it goes on from there.
 * - Aborting the signal only stops LISTENING. To stop the work, call
 *   cancelOperation() (see useOperationStream).
 */

import { createParser } from "eventsource-parser";
import { reportError } from "@/telemetry/telemetry";
import { ApiError } from "./errors";
import type { OperationStreamEvent } from "./generated/types.gen";
import { zOperationStreamEvent } from "./generated/zod.gen";
import { getAccessToken } from "./tokens";

export type StreamEvent = OperationStreamEvent & { seq: number };
export type StreamEventName = OperationStreamEvent["event"];

const TERMINAL_EVENTS: ReadonlySet<string> = new Set(["operation_completed", "operation_failed"]);

export interface StreamOptions {
  signal?: AbortSignal;
  /** Resume after this sequence number (e.g. reattaching after a reload). */
  lastEventId?: number;
  maxRetries?: number;
  /** Base backoff in ms (doubles per attempt, capped at 10s). */
  retryDelayMs?: number;
  /** Test seam (just the call signature). */
  fetchImpl?: (input: URL, init: RequestInit) => Promise<Response>;
  /**
   * The stream starts over from the beginning (events it skipped are gone from the server's buffer): drop what was
   * built from its events so far, which the replay brings back. Without it, those events are left out instead.
   */
  onReset?: () => void;
}

/** Turn one SSE frame into a validated event, or null (reported) if it doesn't match the contract. */
export function toStreamEvent(frame: { event?: string; id?: string; data: string }): StreamEvent | null {
  let data: unknown;
  try {
    data = JSON.parse(frame.data);
  } catch {
    reportError(new Error("Unparseable stream frame"), { source: "stream", event: frame.event ?? "message" });
    return null;
  }
  const parsed = zOperationStreamEvent.safeParse({ event: frame.event ?? "message", data });
  if (!parsed.success) {
    reportError(new Error(`Unknown or malformed stream event "${frame.event}"`), { source: "stream" });
    return null;
  }
  return { ...parsed.data, seq: seqOf(frame) } as StreamEvent;
}

function seqOf(frame: { id?: string }): number {
  const seq = Number(frame.id);
  return Number.isFinite(seq) ? seq : 0;
}

/** A frame's sequence number, and its event: null when it was skipped (reported) for not matching the contract. */
interface Frame {
  seq: number;
  event: StreamEvent | null;
}

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener("abort", () => {
      clearTimeout(timer);
      resolve();
    });
  });

async function* readOnce(response: Response): AsyncGenerator<Frame> {
  const queue: Frame[] = [];
  const parser = createParser({
    onEvent: (frame) => {
      queue.push({ seq: seqOf(frame), event: toStreamEvent(frame) });
    },
  });
  const reader = response.body?.pipeThrough(new TextDecoderStream()).getReader();
  if (!reader) return;
  let ended = false;
  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      parser.feed(value);
      while (queue.length) yield queue.shift() as Frame;
    }
    ended = true;
  } finally {
    // Left before the end (a terminal event, a gap): close the connection rather than leave it open.
    if (!ended) await reader.cancel().catch(() => {});
    reader.releaseLock();
  }
}

/** Retry bookkeeping: exponential backoff (capped at 10s), reset on progress. */
class RetryBudget {
  private attempt = 0;

  constructor(
    private readonly maxRetries: number,
    private readonly baseDelayMs: number,
    private readonly signal?: AbortSignal,
  ) {}

  reset(): void {
    this.attempt = 0;
  }

  /** Wait before the next attempt, or throw `failure()` once the budget is spent. */
  async wait(failure: () => Error): Promise<void> {
    this.attempt += 1;
    if (this.attempt > this.maxRetries) throw failure();
    await sleep(Math.min(this.baseDelayMs * 2 ** (this.attempt - 1), 10_000), this.signal);
  }
}

type FetchImpl = NonNullable<StreamOptions["fetchImpl"]>;

/** Open the SSE connection. Throws ApiError (status 0 for network failures). */
async function openStream(opId: string, lastSeq: number, fetchImpl: FetchImpl, signal?: AbortSignal) {
  const url = new URL(`/api/v1/operations/${encodeURIComponent(opId)}/stream`, globalThis.location.origin);
  if (lastSeq > 0) url.searchParams.set("last_event_id", String(lastSeq));
  const token = await getAccessToken();
  let response: Response;
  try {
    response = await fetchImpl(url, {
      headers: { Accept: "text/event-stream", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      signal,
    });
  } catch (error) {
    throw new ApiError(0, error instanceof Error ? error.message : "Network error");
  }
  if (!response.ok) {
    const body = await response.json().catch(() => undefined);
    throw new ApiError(response.status, response.statusText, body, response.headers.get("x-request-id"));
  }
  return response;
}

/** Client errors (gone, forbidden, not found) won't fix themselves; server/network errors might. */
const isRetryable = (error: unknown) => error instanceof ApiError && (error.status === 0 || error.status >= 500);

/**
 * Relay one connection's events. Returns "done" after a terminal event,
 * "dropped" if the connection ended (or broke) before one, or "gap" at a
 * frame that skips sequence numbers (not relayed). Frames skipped for not
 * matching the contract still count as received.
 */
async function* relay(
  response: Response,
  skips: (seq: number) => boolean,
  onProgress: (seq: number) => void,
): AsyncGenerator<StreamEvent, "done" | "dropped" | "gap"> {
  try {
    for await (const { seq, event } of readOnce(response)) {
      if (skips(seq)) return "gap";
      onProgress(seq);
      if (!event) continue;
      yield event;
      if (TERMINAL_EVENTS.has(event.event)) return "done";
    }
  } catch (error) {
    if (!(error instanceof TypeError || error instanceof DOMException)) throw error;
  }
  return "dropped";
}

/**
 * How often events went missing after each position: asked again from there, then (when the caller can start over)
 * from the start, then left out. A replay from the start numbers from where the server's buffer begins, so only
 * events after one already received can skip.
 */
function gapCounter(asksPerPosition: number) {
  const gaps = new Map<number, number>();
  return {
    /** Whether a frame numbered `seq`, after `last`, skips events and should be asked for again (not relayed). */
    skips(last: number, seq: number): boolean {
      if (last <= 0 || seq <= last + 1) return false;
      const seen = (gaps.get(last) ?? 0) + 1;
      gaps.set(last, seen);
      return seen <= asksPerPosition;
    },
    /** Whether events after `last` went missing again after it was asked for once. */
    missedAgain: (last: number) => (gaps.get(last) ?? 0) > 1,
  };
}

/**
 * Open the stream from `lastSeq`. A failure worth retrying waits out the backoff and gives `undefined`, as does a
 * Stop, so the caller asks again (or ends); any other failure is thrown.
 */
async function openOrWait(
  opId: string,
  lastSeq: number,
  fetchImpl: FetchImpl,
  retry: RetryBudget,
  signal?: AbortSignal,
): Promise<Response | undefined> {
  try {
    return await openStream(opId, lastSeq, fetchImpl, signal);
  } catch (error) {
    if (signal?.aborted) return undefined;
    if (!isRetryable(error)) throw error;
    await retry.wait(() => error as ApiError);
    return undefined;
  }
}

export async function* streamOperation(opId: string, options: StreamOptions = {}): AsyncGenerator<StreamEvent> {
  const { signal, maxRetries = 5, retryDelayMs = 1000, fetchImpl = fetch, onReset } = options;
  const retry = new RetryBudget(maxRetries, retryDelayMs, signal);
  let lastSeq = options.lastEventId ?? 0;
  const onProgress = (seq: number) => {
    if (seq > 0) lastSeq = seq;
    retry.reset(); // progress resets the retry budget
  };
  const gaps = gapCounter(onReset ? 2 : 1);
  const skips = (seq: number) => gaps.skips(lastSeq, seq);

  while (!signal?.aborted) {
    // Sequential by design: each retry waits for the previous attempt's backoff.
    const response = await openOrWait(opId, lastSeq, fetchImpl, retry, signal); // NOSONAR
    if (!response) continue;
    const outcome = yield* relay(response, skips, onProgress);
    if (outcome === "done" || signal?.aborted) return;
    if (outcome === "gap") {
      // Events went missing: ask again from the last one received, straight away (nothing failed). Missing again,
      // the server's buffer no longer has them: start over, dropping what was built from the stream so far.
      if (gaps.missedAgain(lastSeq)) {
        lastSeq = 0;
        onReset?.();
      }
      continue;
    }
    // The connection dropped before a terminal event: resume from lastSeq.
    await retry.wait(() => new ApiError(0, "Stream disconnected")); // NOSONAR
  }
}
